// Pure, framework-agnostic logic for the admin user table:
// filtering, pagination and selection. Extracted from AdminPage.tsx so the
// same code the UI runs can be unit-tested in isolation.

export const PAGE_SIZE = 25;

/** Case-insensitive substring match on email. Empty search returns all. */
export function filterUsers<T extends { email: string }>(users: T[], search: string): T[] {
  const q = search.toLowerCase();
  return users.filter((u) => u.email.toLowerCase().includes(q));
}

/** Number of pages for a given item count. Always at least 1. */
export function totalPages(count: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

/** Slice of items for a 1-based page. Out-of-range pages return []. */
export function pageSlice<T>(items: T[], page: number, pageSize = PAGE_SIZE): T[] {
  return items.slice((page - 1) * pageSize, page * pageSize);
}

/** Toggle membership of a value in a Set, returning a NEW Set (immutable). */
export function toggleInSet(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** Toggle a group of values as a block: if all are present, remove them all; otherwise add them all. Returns a new Set. */
export function toggleAll(set: Set<string>, values: string[]): Set<string> {
  const next = new Set(set);
  const allPresent = values.length > 0 && values.every((v) => next.has(v));
  if (allPresent) {
    values.forEach((v) => next.delete(v));
  } else {
    values.forEach((v) => next.add(v));
  }
  return next;
}

/** Build a selection Set from a list of emails. */
export function selectAll(emails: string[]): Set<string> {
  return new Set(emails);
}

/** Parse pasted emails: split on newline/comma/semicolon/whitespace, trim, drop empties. */
export function parseEmails(raw: string): string[] {
  return raw.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
}
